// global styles
import GlobalStyles from '../components/layout/global-styles'
// react-responsive-carousel
import 'react-responsive-carousel/lib/styles/carousel.min.css'

import App from 'next/app'
import Head from 'next/head'
import { useEffect, useRef } from 'react'
import Cookie from 'universal-cookie'
import { QueryClient, QueryClientProvider, HydrationBoundary } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { UAParser } from 'ua-parser-js'
import { init } from '../../sentry'
import { tokenKey } from '../api-client/client'
import { appWithTranslation } from 'next-i18next'
import i18nConfig from '../../internalization'
import protectRoutes from '../utils/protect-routes'
import Layout from '../components/layout'
import { DeviceTypeProvider } from '../contexts/device-type'
import { AuthProvider } from '../contexts/auth'
import * as gTag from '../utils/g-tag'
import { DeviceType } from '../constants'

const QUERY_CLIENT_OPTIONS = {
  defaultOptions: {
    queries: {
      retry: 0,
      refetchOnWindowFocus: false,
    },
    mutations: {
      onError: (e) => {
        if (process.env.NODE_ENV === 'development') alert(JSON.stringify(e, null, 2))
      },
    },
  },
}

const Noop = ({ children }) => <>{children}</>

function EnhancedApp({ Component, pageProps, router, deviceType, accessToken }) {
  const ContextProvider = Component.provider || Noop
  const getLayout = Component.getLayout || ((page) => <Layout>{page}</Layout>)

  const queryClientRef = useRef()
  if (!queryClientRef.current) {
    queryClientRef.current = new QueryClient(QUERY_CLIENT_OPTIONS)
  }

  const generateAlternateTags = (lang) => {
    const { pathname } = router
    const href = `${process.env.NEXT_PUBLIC_DOMAIN}/${lang}${pathname === '/' ? '' : pathname}`
    return <link key={lang} rel='alternate' hrefLang={lang} href={href} />
  }

  useEffect(() => {
    if (/iPad|iPhone|iPod/.test(window.navigator.userAgent)) {
      const meta = document.createElement('meta')
      meta.setAttribute('name', 'viewport')
      meta.content = 'width=device-width, initial-scale=1, maximum-scale=1'

      document.getElementsByTagName('head')[0].appendChild(meta)
    }
  }, [])

  useEffect(() => {
    const handleRouteChange = (url) => {
      try {
        //this can throw an exception when blocked by adblockers
        gTag.pageView(url)
      } catch {}
    }

    router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router.events])

  return (
    <>
      <Head>{(i18nConfig.i18n.locales).map(generateAlternateTags)}</Head>
      <QueryClientProvider client={queryClientRef.current}>
        <HydrationBoundary state={pageProps.dehydratedState}>
          <DeviceTypeProvider
            deviceType={
              [DeviceType.Mobile, DeviceType.Tablet].includes(deviceType)
                ? DeviceType.Mobile
                : DeviceType.Desktop
            }
          >
            <AuthProvider initialState={{ accessToken }}>
              <ContextProvider>
                <GlobalStyles>{getLayout(<Component {...pageProps} />)}</GlobalStyles>
              </ContextProvider>
            </AuthProvider>
          </DeviceTypeProvider>
        </HydrationBoundary>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </>
  )
}

EnhancedApp.getInitialProps = async (appContext) => {
  const appInitialProps = await App.getInitialProps(appContext)
  const deviceType = new UAParser(appContext.ctx.req?.headers['user-agent']).getDevice().type
  const accessToken = new Cookie(appContext.ctx.req?.headers.cookie).get(tokenKey)
  const { isProtected } = appInitialProps.pageProps

  protectRoutes({ appContext, isProtected })

  return { ...appInitialProps, deviceType, accessToken }
}

init()

export default appWithTranslation(EnhancedApp, i18nConfig)
